import React from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components"
import { faCheck, faFileAlt, faPen } from "@fortawesome/free-solid-svg-icons"
import devices from "styles/devices"
import { Button, ButtonType } from "components/atoms/Button"
import { Card } from "components/atoms/Card"
import Typography from "components/_blueprint/Typography/Typography"
import { formatAppointmentDateTime } from "lib/FormatDate"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { enterSplitFactFind, SplitFfEntrySource } from "lib/SplitFactFind"
import { HandoffFields } from "lib/JourneyHandoff"
import { EventAction, EventCategory, EventLabel } from "models/GoogleAnalytics"
import { ConveyancingCardComponent } from "./components/ConveyancingCard"
import { ProgressRing } from "./components/ProgressRing"
import { RateCheckPromiseCard } from "./components/RateCheckPromiseCard"
import { SolidIcon } from "./components/SolidIcon"
import { WhatToExpectNextCard } from "./components/WhatToExpectNextCard"
import { SmallPrint, StatusContainer } from "./shared/StyledComponents"

interface PrepareForAppointmentViewProps {
  friendlyId: string
  appointmentDate: Date
  /** Who the appointment is with, as it is on the booking */
  expertName?: string
  /** Whether the split fact find ("mortgage profile") has already been completed */
  profileComplete: boolean
  /** Whether every applicant has uploaded the documents that are asked for before the appointment */
  documentsComplete?: boolean
  /** The property's postcode. The split fact find can't be opened without it, so it isn't offered */
  postcode?: string
  /** What this app already knows that the split fact find can use, handed to it when it is opened */
  handoff?: HandoffFields
}

// The fact find and the booking are done by the time this is shown, which is where the ring
// starts. What is left is the things this page asks for, which share the rest of it equally.
const PROGRESS_AFTER_BOOKING = 80
const PROGRESS_REMAINING = 20

const CardStyleOverrides = {
  borderRadius: "24px",
}

const CardHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    "heading ring"
    "text text";
  align-items: start;
  column-gap: 16px;
  row-gap: 12px;

  @media screen and ${devices.bp_sm} {
    grid-template-areas:
      "heading ring"
      "text ring";
    align-items: center;
    row-gap: 8px;
  }
`

const HeadingBlock = styled.div`
  grid-area: heading;
`

const IntroText = styled.div`
  grid-area: text;
`

const RingSlot = styled.div`
  grid-area: ring;
`

const CardHeading = styled.span`
  display: block;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};

  @media screen and ${devices.bp_sm} {
    font-size: 24px;
  }
`

const Tiles = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 24px;

  @media screen and ${devices.bp_sm} {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.background.secondary};

  &:only-child {
    grid-column: 1 / -1;
  }
`

const TileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`

const TileTitle = styled.h3`
  margin: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`

const TileAction = styled.div`
  margin-top: auto;
  padding-top: 8px;

  /*
   * White against the tile, rather than see-through (the button's own hover style still applies),
   * and less side padding so the longer labels stay on one line on a phone.
   */
  button.outline {
    padding: 10px 16px;
    background: ${({ theme }) => theme.colors.background.primary};
  }
`

const CompleteBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 56px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.status.success};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  font-size: 14px;
`

const firstNameOf = (name?: string) => name?.trim().split(/\s+/)[0] || undefined

export const PrepareForAppointmentView: React.FC<PrepareForAppointmentViewProps> = ({
  friendlyId,
  appointmentDate,
  expertName,
  profileComplete,
  documentsComplete,
  postcode,
  handoff,
}) => {
  const history = useHistory()

  const expertFirstName = firstNameOf(expertName)
  const appointmentHeading = `${formatAppointmentDateTime(appointmentDate)}${
    expertName ? ` with ${expertName}` : ""
  }`
  const showProfileTile = profileComplete || !!postcode
  // A profile that can't be offered at all (no postcode) isn't something they can tick off, so it
  // isn't counted and the ring can still fill.
  const outstanding = showProfileTile ? [profileComplete, documentsComplete] : [documentsComplete]
  const percentComplete =
    PROGRESS_AFTER_BOOKING +
    Math.round((PROGRESS_REMAINING * outstanding.filter(Boolean).length) / outstanding.length)

  const handleFinishProfile = () => {
    // The button is only shown with a postcode, and the split fact find can't be opened without one
    if (!postcode) return

    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: EventLabel.finishMortgageProfile,
    })
    enterSplitFactFind({ friendlyId, postcode, source: SplitFfEntrySource.myMojo, handoff })
  }

  const handleUploadDocuments = () => {
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: EventLabel.uploadDocs,
    })
    history.push(`/application/${friendlyId}/documents`)
  }

  return (
    <StatusContainer>
      <div>
        <Typography.Body weight={500} margin="0 0 4px 0">
          You’re all booked in
        </Typography.Body>
        <Typography.H3 as="h1" margin="0 0 8px 0">
          {appointmentHeading}
        </Typography.H3>
        <Typography.Body color="secondary" margin="0">
          You’ll soon be getting free expert advice on the best mortgage option for your specific
          circumstances.
        </Typography.Body>
      </div>

      <Card
        theme="default"
        style={CardStyleOverrides}
        content={
          <>
            <CardHeader>
              <HeadingBlock>
                <CardHeading>You’re one step closer…</CardHeading>
                <CardHeading as="h2">Prepare for your upcoming appointment</CardHeading>
              </HeadingBlock>
              <IntroText>
                <Typography.Body color="secondary" margin="0">
                  To ensure {expertFirstName ?? "your expert"} can provide you with accurate advice,
                  there are a couple of quick things you can do prior to the appointment.
                </Typography.Body>
              </IntroText>
              <RingSlot>
                <ProgressRing percent={percentComplete} caption="Mortgage Profile" />
              </RingSlot>
            </CardHeader>

            <Tiles>
              {showProfileTile && (
                <Tile data-testid="profile-tile">
                  <TileHeader>
                    <IconSlot>
                      <SolidIcon icon={faPen} />
                    </IconSlot>
                    <TileTitle>
                      {profileComplete
                        ? "Extra questions answered"
                        : "Complete your mortgage profile"}
                    </TileTitle>
                  </TileHeader>
                  <Typography.SmallBody color="secondary" margin="0">
                    {profileComplete
                      ? "Thank you. This helps your Mortgage Expert spend more time discussing the stuff that matters."
                      : "Completing your mortgage profile helps to save time on your call and get the most accurate advice."}
                  </Typography.SmallBody>
                  <TileAction>
                    {profileComplete ? (
                      <CompleteBar>
                        <SolidIcon icon={faCheck} />
                        Complete
                      </CompleteBar>
                    ) : (
                      <Button
                        type={ButtonType.Outline}
                        width="100%"
                        text="Finish Mortgage Profile"
                        onClick={handleFinishProfile}
                      />
                    )}
                  </TileAction>
                </Tile>
              )}

              <Tile data-testid="documents-tile">
                <TileHeader>
                  <IconSlot>
                    <SolidIcon icon={faFileAlt} />
                  </IconSlot>
                  <TileTitle>
                    {documentsComplete ? "Documents uploaded" : "Upload your documents"}
                  </TileTitle>
                </TileHeader>
                <Typography.SmallBody color="secondary" margin="0">
                  {documentsComplete
                    ? "Thank you. Your Expert has what they need to research eligible deals."
                    : "These essential documents help your Expert to research eligible deals and are used for any future application."}
                </Typography.SmallBody>
                <TileAction>
                  {documentsComplete ? (
                    <CompleteBar>
                      <SolidIcon icon={faCheck} />
                      Complete
                    </CompleteBar>
                  ) : (
                    <Button
                      type={ButtonType.Outline}
                      width="100%"
                      text="Upload documents"
                      onClick={handleUploadDocuments}
                    />
                  )}
                </TileAction>
              </Tile>
            </Tiles>
          </>
        }
      />

      <WhatToExpectNextCard />

      <ConveyancingCardComponent friendlyId={friendlyId} />

      <RateCheckPromiseCard friendlyId={friendlyId} />

      <SmallPrint>
        <p>
          If for any reason you need to amend or cancel your appointment, contact your expert
          directly via email.
        </p>
      </SmallPrint>
    </StatusContainer>
  )
}
